<template>
  <div
    v-if="editor"
    :id="id"
    class="custom-editor"
    :class="{ 'custom-editor_read': typeRead }"
    :type="type"
  >
    <EditorMenuBar
      v-if="!typeRead && !taskInput"
      :id="id"
      :editor="editor"
      :taskEditor="taskEditor"
      :inComment="inComment"
      :targetConfig="targetConfig"
      :referenceFileTargetConfig="referenceFileTargetConfig"
      :isReadonly="isReadonly"
      class="custom-editor__header"
      :class="{ 'is-disabled': isReadonly, 'custom-editor__header--comment': inComment }"
      @focus.stop
    >
      <template #after>
        <slot name="after" />
      </template>
    </EditorMenuBar>

    <div class="custom-editor__content">
      <div class="custom-editor__content-in">
        <EditorContent
          :editor="editor"
          class="custom-editor__in"
        />
        <slot
          name="footer"
          :editor="editor"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import Placeholder from "@tiptap/extension-placeholder"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import TextAlign from "@tiptap/extension-text-align"
import TextStyle from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import { Editor, EditorContent, Extension } from "@tiptap/vue-3"
import { Getter } from "s-vuex-class"

import EditorMenuBar from "@/components/Editor/EditorMenuBar.vue"

import { TypeConfig } from "@/models"
import { EditorData } from "@/models/EditorData"
import { ClearStoredLink } from "@/plugins/extensions/ClearStoredLink"
import { Color } from "@/plugins/extensions/Color"
import { CustomHeader } from "@/plugins/extensions/Header"
import { NonInclusiveLink } from "@/plugins/extensions/NonInclusiveLink"
import { ParagraphWithDataTitle } from "@/plugins/extensions/ParagraphWithDataTitle"
import { PasteColorSanitizer } from "@/plugins/extensions/PasteColorSanitizer"
import { StarterKitCustomHeading } from "@/plugins/extensions/StarterKitCustomHeading"
import { Getters } from "@/store"
import { handleMouseScroll } from "@/utils/scrollUtils"

import { InitComponent } from "../Reference"
import EditorAttachment from "./EditorAttachment.vue"

@Options({
  name: "EditorComponent",
  components: {
    EditorContent,
    EditorMenuBar
  }
})
export default class EditorComponent extends Vue {
  @Prop({
    type: String,
    default: ""
  })
  readonly id!: string
  @Prop({
    type: String,
    default: ""
  })
  value: string
  @Prop({ default: null }) targetConfig: TypeConfig | null
  @Prop({ default: null }) referenceFileTargetConfig: TypeConfig | null
  @Prop() file: { url: string; type: string }
  @Prop({ default: "edit" }) readonly type: string
  @Prop({ default: "" }) readonly placeholder: string
  @Prop({ default: false }) readonly focusEditor: boolean
  @Prop({ default: false }) readonly taskEditor: boolean
  @Prop({ default: false }) readonly inComment: boolean
  @Prop({ default: false }) readonly taskInput: boolean
  @Prop({ default: false }) readonly addSpace: boolean
  @Prop({ default: true }) readonly isAttachmentResizable: boolean
  @Prop({ default: () => [] }) readonly extensions: []
  @Prop({ default: false }) readonly triggerHandleInputOnMount: boolean
  @Prop({ default: false }) readonly isReadonly: boolean

  editor: Editor | null = null
  @Getter(Getters.ATTACHMENT_UPLOAD) attachmentIds: string[]

  customKeyboardShortcuts = Extension.create({})
  editorData: EditorData

  mounted() {
    if (this.focusEditor) {
      this.$nextTick(() => {
        this.focus()
      })
    }
    if (this.taskInput) {
      this.customKeyboardShortcuts = Extension.create({
        addKeyboardShortcuts() {
          return {
            Enter: () => true
          }
        }
      })
    }
    this.init()
  }

  @Watch("focusEditor")
  focus() {
    if (!this.focusEditor || !this.editor) return

    const { state, commands } = this.editor

    let foundPos: number | null = null

    state.doc.descendants((node, pos) => {
      if (foundPos !== null) return false
      if (node.attrs?.dataTitle === "title") {
        foundPos = pos + node.nodeSize - 1
      }

      return true
    })

    this.$nextTick(() => {
      if (foundPos !== null) {
        commands.focus(foundPos)
      } else {
        commands.focus("end")
      }
    })
  }

  init() {
    this.editorData = new EditorData({
      USER: InitComponent,
      PROJECT: InitComponent,
      EPIC: InitComponent,
      IDEA: InitComponent,
      MILESTONE: InitComponent,
      TEAM: InitComponent,
      SPRINT: InitComponent,
      ITEM: InitComponent,
      TASK: InitComponent,
      ATTACHMENT: EditorAttachment
    })

    const extensions = [
      StarterKitCustomHeading.configure({
        levels: [1, 2]
      }),
      StarterKit.configure({
        code: false,
        heading: false,
        paragraph: false
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      NonInclusiveLink.configure({
        HTMLAttributes: {
          class: "link is-medium"
        },
        validate: href => /^https?:\/\//.test(href)
      }),
      ClearStoredLink,
      Underline,
      Placeholder.configure({
        placeholder: this.placeholder
      }),
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableHeader,
      TableCell,
      this.customKeyboardShortcuts,
      TextStyle,
      Color,
      PasteColorSanitizer,
      ParagraphWithDataTitle,
      CustomHeader
    ]

    const mergedExtensions = [...extensions, ...this.extensions].filter(
      (extension, index, allExtensions) =>
        index === allExtensions.findIndex(({ name }) => name === extension.name)
    )

    this.editor = new Editor({
      extensions: mergedExtensions,
      content: this.value,
      onCreate: () => {
        if (this.triggerHandleInputOnMount) {
          this.createData()
        }
      },
      onUpdate: () => {
        this.handleInput()
      },
      onFocus: event => {
        this.$emit("focus", {
          event: event
        })
      },
      onBlur: event => {
        this.$emit("blur", {
          event: event
        })
      },
      editorProps: {
        attributes: {
          class: "custom-editor__prose-mirror custom-scroll"
        }
      }
    })
    this.onReferenceFileTargetConfigChange()
    if (this.typeRead || this.isReadonly) {
      this.editor.setEditable(false)
    }
    this.editor.view.dom.addEventListener("dragover", this.handleDrag)
  }

  @Watch("referenceFileTargetConfig", { deep: true })
  onReferenceFileTargetConfigChange() {
    if (!this.editor) return

    Object.assign(this.editor, {
      referenceFileTargetConfig: this.referenceFileTargetConfig
    })
  }

  handleDrag(event) {
    if (!this.editor) return
    const container = this.editor.view.dom
    handleMouseScroll({
      event,
      container,
      threshold: 100,
      maxScrollSpeed: 20
    })
  }

  get typeRead(): boolean {
    return this.type === "read"
  }

  beforeUnmount() {
    if (!this.editor) return

    this.editor.view.dom.removeEventListener("dragover", this.handleDrag)
    this.editor.destroy()
  }

  @Emit()
  handleInput() {
    if (!this.editor) {
      return
    }
    this.editorData.setText(this.editor.getText())
    this.editorData.setJson(this.editor.getJSON())
    this.editorData.setHtml(this.editor.getHTML())
    return this.editorData
  }

  @Emit()
  createData() {
    if (!this.editor) {
      return
    }
    this.editorData.setText(this.editor.getText())
    this.editorData.setJson(this.editor.getJSON())
    this.editorData.setHtml(this.editor.getHTML())
    return this.editorData
  }

  @Watch("value")
  onValueChange(newValue: string) {
    if (this.editor && newValue !== this.editor.getHTML()) {
      this.editor.commands.setContent(newValue)
    }
  }
}
</script>
